import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {SettingsStackParamList} from '@/app/routes';
import {
  createApiToken,
  fetchApiBridgeSettings,
  fetchDesktops,
  revokeApiToken,
} from '@/entities/settings/api';
import {useAuthStore} from '@/entities/session/auth-store';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {ApiError} from '@/shared/api';
import {formatTimestamp} from '@/shared/lib/format';
import {Badge} from '@/shared/ui/badge';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Field, FieldGroup} from '@/shared/ui/field';
import {AppInput} from '@/shared/ui/input';
import {ListRow, PagedRows} from '@/shared/ui/list-row';
import {Page} from '@/shared/ui/page';
import {SettingsNav} from '@/pages/settings/SettingsNav';
import {Section} from '@/shared/ui/section';
import {Sheet} from '@/shared/ui/sheet';
import {Skeleton} from '@/shared/ui/skeleton';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * The desktop API bridge: how the SamVision desktop app and the terminals on the LAN
 * reach this company, and the tokens that authenticate them.
 *
 * The one rule that governs the whole screen: **a minted token's secret exists exactly
 * once.** `POST /control/settings/api/tokens` is the only response that ever carries
 * it; the list route returns a prefix and nothing else. So the sheet that mints one
 * stays open showing the secret until the user dismisses it deliberately — closing on
 * success, which is what every other create-sheet in this app does, would throw the
 * value away with no way to get it back except minting another.
 *
 * Revoking is offered on every token including the ones issued to terminals at
 * adoption ("device: …"), because those are exactly the ones that need revoking when a
 * terminal is lost — but the list says which is which, so it is never a surprise.
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'ApiBridge'>;

const styles = StyleSheet.create({
  form: {gap: spacing(3)},
  secret: {gap: spacing(2)},
});

export function ApiBridgeScreen(_: Props) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const companyId = useAuthStore(s => s.activeContext?.companyId);

  const settings = useQuery({
    queryKey: ['api-bridge', companyId],
    queryFn: () => fetchApiBridgeSettings(companyId as string),
    enabled: !!companyId,
  });

  const desktops = useQuery({
    queryKey: ['desktops', companyId],
    queryFn: () => fetchDesktops(companyId as string),
    enabled: !!companyId,
  });

  const [minting, setMinting] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [secret, setSecret] = useState<string | null>(null);

  const mint = useMutation({
    mutationFn: () => createApiToken(companyId as string, tokenName.trim()),
    onSuccess: async result => {
      // Deliberately does NOT close the sheet — see the module note.
      setSecret(result.token ?? null);
      setTokenName('');
      await qc.invalidateQueries({queryKey: ['api-bridge', companyId]});
    },
  });

  const revoke = useMutation({
    meta: {toast: 'toast.tokenRevoked'},
    mutationFn: (tokenId: string) => revokeApiToken(companyId as string, tokenId),
    onSuccess: () => qc.invalidateQueries({queryKey: ['api-bridge', companyId]}),
  });

  if (!companyId) {
    return <ContextPlaceholder titleKey="more.apiBridge" />;
  }

  const tokens = settings.data?.tokens ?? [];

  return (
    <Page
      sideNav={<SettingsNav />}
      width="form"
      scroll
      testID="api-bridge"
      title={t('more.apiBridge')}
      subtitle={t('apiBridge.intro')}
      >

      <Section title={settings.data ? t('apiBridge.endpoint') : undefined}>
        {settings.isPending ? (
          <Skeleton height={200} radius={radii.lg} />
        ) : settings.isError ? (
          <Banner tone="error" title={t('errors.generic')} />
        ) : (
          <FieldGroup>
            <Field label={t('apiBridge.desktopName')} value={settings.data.desktop_name} />
            <Field label={t('apiBridge.localUrl')} value={settings.data.local_url} mono />
            <Field
              label={t('apiBridge.lanUrl')}
              value={settings.data.current_lan_url ?? '—'}
              mono
              note={t('apiBridge.lanUrlNote')}
            />
            <Field
              label={t('apiBridge.pollAfter')}
              value={t('apiBridge.seconds', {count: settings.data.poll_after_seconds})}
            />
            <Field
              label={t('apiBridge.onlineThreshold')}
              value={t('apiBridge.seconds', {
                count: settings.data.online_threshold_seconds,
              })}
              note={t('apiBridge.onlineThresholdNote')}
            />
          </FieldGroup>
        )}
      </Section>

      <Section
        title={t('apiBridge.tokens')}
        hint={t('apiBridge.tokensHint')}
        actionLabel={t('apiBridge.mint')}
        onAction={() => {
          setSecret(null);
          setMinting(true);
        }}>
        {tokens.length === 0 ? (
          <EmptyState title={t('apiBridge.noTokens')} />
        ) : (
          <PagedRows
            items={tokens}
            keyOf={token => token.token_id}
            testID="tokens-list"
            render={token => (
              <ListRow
                icon="lock"
                title={token.name}
                subtitle={t('apiBridge.tokenMeta', {
                  prefix: token.token_prefix,
                  used: token.last_used_at
                    ? formatTimestamp(token.last_used_at)
                    : t('apiBridge.neverUsed'),
                })}
                trailing={
                  <StatusPill
                    tone={token.active ? 'granted' : 'neutral'}
                    size="sm"
                    label={t(token.active ? 'apiBridge.active' : 'apiBridge.revoked')}
                  />
                }
                onPress={
                  token.active ? () => revoke.mutate(token.token_id) : undefined
                }
                testID={`token-${token.token_id}`}
              />
            )}
          />
        )}
        {revoke.isError ? (
          <Banner
            tone="error"
            title={
              revoke.error instanceof ApiError
                ? revoke.error.message
                : t('errors.generic')
            }
          />
        ) : null}
      </Section>

      <Section title={t('apiBridge.desktops')} hint={t('apiBridge.desktopsHint')}>
        {desktops.isPending ? (
          <Skeleton height={90} radius={radii.lg} />
        ) : (desktops.data?.desktops.length ?? 0) === 0 ? (
          <EmptyState title={t('apiBridge.noDesktops')} />
        ) : (
          <PagedRows
            items={desktops.data?.desktops ?? []}
            keyOf={desktop => desktop.install_id}
            testID="desktops-list"
            render={desktop => (
              <ListRow
                icon="devices"
                title={desktop.name || desktop.hostname}
                /* An unlinked installation says WHEN and by whose account it left,
                   in place of the routine "last seen": that machine is not coming
                   back, so when it was last awake stops being the useful fact. */
                subtitle={
                  desktop.unlinked_at
                    ? t('apiBridge.desktopUnlinkedMeta', {
                        when: formatTimestamp(desktop.unlinked_at),
                        who: desktop.unlinked_by || t('apiBridge.unlinkedByUnknown'),
                      })
                    : t('apiBridge.desktopMeta', {
                        os: desktop.os || '—',
                        version: desktop.app_version || '—',
                        seen: formatTimestamp(desktop.last_seen_at),
                      })
                }
                trailing={
                  /* `unlinked_at` outranks `online`. A desktop that left is usually
                     offline too, but that is a consequence, not the fact — and
                     "офлайн" would read as "switched off for the night" about a
                     machine that walked out with the catalogue. */
                  desktop.unlinked_at ? (
                    <Badge
                      variant="warn"
                      size="sm"
                      label={t('apiBridge.desktopUnlinked')}
                    />
                  ) : (
                    <StatusPill
                      tone={desktop.online ? 'online' : 'offline'}
                      size="sm"
                      label={t(desktop.online ? 'home.devices.online' : 'home.devices.offline')}
                    />
                  )
                }
                testID={`desktop-${desktop.install_id}`}
              />
            )}
          />
        )}
      </Section>

      <Sheet
        visible={minting}
        onClose={() => {
          setMinting(false);
          setSecret(null);
          mint.reset();
        }}
        title={t('apiBridge.mint')}
        subtitle={t('apiBridge.mintHint')}
        testID="mint-token-sheet">
        <View style={styles.form}>
          {secret ? (
            <View style={styles.secret}>
              <Banner
                tone="warning"
                title={t('apiBridge.secretOnce')}
                message={t('apiBridge.secretOnceHint')}
              />
              <Card padding={3} variant="flat">
                <AppText variant="mono" selectable testID="minted-token">
                  {secret}
                </AppText>
              </Card>
              <AppButton
                title={t('actions.done')}
                onPress={() => {
                  setMinting(false);
                  setSecret(null);
                  mint.reset();
                }}
                testID="minted-token-done"
              />
            </View>
          ) : (
            <>
              <AppInput
                label={t('apiBridge.tokenName')}
                value={tokenName}
                onChangeText={setTokenName}
                hint={t('apiBridge.tokenNameHint')}
                testID="token-name"
              />
              {mint.isError ? (
                <Banner
                  tone="error"
                  title={
                    mint.error instanceof ApiError
                      ? mint.error.message
                      : t('errors.generic')
                  }
                />
              ) : null}
              <AppButton
                title={t('apiBridge.mint')}
                disabled={tokenName.trim().length === 0}
                loading={mint.isPending}
                onPress={() => mint.mutate()}
                testID="mint-token"
              />
            </>
          )}
        </View>
      </Sheet>
    </Page>
  );
}
